import { Grid, GridProps } from '@progress/kendo-react-grid';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import cx from 'classnames';
import React, { MutableRefObject, PropsWithChildren, useCallback, useLayoutEffect, useRef } from 'react';
import { intl } from '../../../common/intl';
import { withThemeNext } from '../../layout/theme-next';
import { Translation } from '../../translations/types';
import { StyledKendoGridProps } from './StyledKendoGrid.types';

const formatMessage = (id: Translation) => intl.formatMessage({ id });

loadMessages({
  grid: {
    filterBooleanAll: formatMessage('common.all'),
    filterIsTrue: formatMessage('common.true'),
    filterIsFalse: formatMessage('common.false'),
    groupPanelEmpty: formatMessage('kendo.groupPanelEmpty'),
  },
}, intl.locale);

const StyledKendoGrid: React.FC<PropsWithChildren<StyledKendoGridProps>> = ({
  children,
  hasBoxScrollbars,
  containerRef,
  onRowClick,
  rowRender,
  ...props
}, ref: MutableRefObject<Grid>) => {
  const fallbackContainerRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = containerRef ?? fallbackContainerRef;
  const scheduleMeasurement = useRef<() => void>();
  const boxScrollbars = useRef(hasBoxScrollbars);

  useLayoutEffect(() => {
    boxScrollbars.current = hasBoxScrollbars;
    const content = gridContainerRef.current?.querySelector('.k-grid-content');
    content?.classList.toggle('containing-box-scrollbar', Boolean(hasBoxScrollbars));
    scheduleMeasurement.current?.();
  }, [hasBoxScrollbars, gridContainerRef]);

  useLayoutEffect(() => {
    const container = gridContainerRef.current;
    if (!container) {
      return;
    }

    let frame: number | undefined;
    let observedElements = new Set<Element>();
    const measure = () => {
      frame = undefined;
      // Resolve after commit, including when Kendo replaces the content/header.
      const content = container.querySelector<HTMLDivElement>('.k-grid-content');
      const header = container.querySelector<HTMLDivElement>('.k-grid-header');
      content?.classList.toggle('containing-box-scrollbar', Boolean(boxScrollbars.current));

      if (resizeObserver) {
        const elements = new Set<Element>([container]);
        if (content) {
          elements.add(content);
          // The table (or virtual scroller) can grow without resizing its viewport.
          Array.from(content.children).forEach((child) => elements.add(child));
        }
        observedElements.forEach((element) => {
          if (!elements.has(element)) {
            resizeObserver.unobserve(element);
          }
        });
        elements.forEach((element) => {
          if (!observedElements.has(element)) {
            resizeObserver.observe(element);
          }
        });
        observedElements = elements;
      }

      if (content && header) {
        const padding = `${content.offsetWidth - content.clientWidth}px`;
        if (header.style.paddingInlineEnd !== padding) {
          header.style.paddingInlineEnd = padding;
        }
      }
    };
    const schedule = () => {
      if (frame === undefined) {
        frame = window.requestAnimationFrame(measure);
      }
    };
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver(schedule);
    const mutationObserver = typeof MutationObserver === 'undefined'
      ? undefined
      : new MutationObserver(schedule);

    scheduleMeasurement.current = schedule;
    measure();
    mutationObserver?.observe(container, { childList: true, characterData: true, subtree: true });
    window.addEventListener('resize', schedule);
    container.addEventListener('load', schedule, true);

    return () => {
      scheduleMeasurement.current = undefined;
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', schedule);
      container.removeEventListener('load', schedule, true);
      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [gridContainerRef]);

  useLayoutEffect(() => {
    // Older browsers still measure after React updates and window resizes.
    if (typeof ResizeObserver === 'undefined') {
      scheduleMeasurement.current?.();
    }
  });

  const renderRow = useCallback<NonNullable<GridProps['rowRender']>>((row, rowProps) => {
    const rowElement = onRowClick
      ? React.cloneElement(row, { className: cx(row.props.className, 'k-clickable-row') })
      : row;

    return rowRender?.(rowElement, rowProps) ?? rowElement;
  }, [onRowClick, rowRender]);

  return (
    <LocalizationProvider language={intl.locale}>
      <div
        ref={gridContainerRef}
        style={props.style}
      >
        <Grid
          {...props}
          ref={ref}
          rowRender={renderRow}
          onRowClick={onRowClick}
        >
          {children}
        </Grid>
      </div>
    </LocalizationProvider>
  );
};

export default withThemeNext(React.forwardRef(StyledKendoGrid));
